/* eslint-disable max-lines -- simple component with a lot of fields to display */
import ArticleIcon from '@mui/icons-material/Article';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import DetailsIcon from '@mui/icons-material/Info';
import FeesIcon from '@mui/icons-material/LocalOffer';
import PlansIcon from '@mui/icons-material/ShoppingCart';
import Timeline from '@mui/icons-material/Timeline';
import AgreementsIcon from '@mui/icons-material/Tune';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { FC } from 'react';
import {
  BooleanField,
  NumberField,
  SelectField,
  TextField,
  useGetIdentity,
  useTranslate,
  WithRecord,
} from 'react-admin';
import { match } from 'ts-pattern';

import { getContractTypeDefinition } from '@Common/types/bo/b2b/contracts/contract_type_definitions';
import { GetBoExternalContractsOneResponse } from '@Common/types/bo_external/contracts';
import { isERContract } from '@Common/validators/contracts';

import {
  b2bMatchingContributionPaymentFrequencyChoices,
  contractRelationshipTypeChoices,
  contractStatusChoices,
  contractTeamChoices,
  contractTypeChoices,
  paperTransactionStatementFrequencyChoices,
} from '@sh4res/bo-common/choices/contracts';
import { feePaidByChoices } from '@sh4res/bo-common/choices/plans';
import {
  B2bContractAgreements,
  B2bPlans,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import {
  CurrencyField,
  EmailStateChip,
  IdField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { B2BInvestorProfileVerticalTabContent } from '@sh4res/bo-common/resources/investor_profile/components/B2BInvestorProfileVerticalTab/B2BInvestorProfileVerticalTabContent';
import { isB2bDistributor } from '@sh4res/bo-common/utils/b2b_auth_role';
import { useCountryCodeChoicesByName } from '@sh4res/bo-common/utils/countryChoices';

import { ContractsDocumentsList } from '@Resources/contracts/tabs/ContractsDocumentsList';
// eslint-disable-next-line max-lines-per-function -- simple component
export const ContractsShow: FC = () => {
  const testId = 'BoExternalContractsShow';
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();

  const countryCodeChoicesByName = useCountryCodeChoicesByName();

  return (
    <ShowWithNavigation
      avatar={{ name_source: 'contract_number' }}
      actions={false}
      title={
        <WithRecord
          render={(record: GetBoExternalContractsOneResponse) => record.contract_number}
        />
      }
      sx={{ height: '100%' }}>
      <WithRecord
        // eslint-disable-next-line max-lines-per-function -- simple component
        render={(record: GetBoExternalContractsOneResponse) => {
          const { allowsProcessingOptionsByCashSource } = getContractTypeDefinition(
            record.contract_type,
          ).campaigns;

          return (
            <VerticalTabsLayout>
              <VerticalTab
                icon={<DetailsIcon />}
                data-testid="BoExternalContractsShow_DetailsVerticalTab"
                label="resources.bo_common.b2b.contracts.menus.details"
                path="details">
                <ShowSection label="general.sections.details">
                  <ShowFields<GetBoExternalContractsOneResponse>
                    fields={{
                      ...(isB2bDistributor(identity) && {
                        id: <IdField reference="contracts" data-testid={`${testId}_IdField`} />,
                        b2b_company_id: (
                          <IdField reference="companies" data-testid={`${testId}_CompanyIdField`} />
                        ),
                      }),
                      team: (
                        <SelectField
                          choices={contractTeamChoices}
                          data-testid={`${testId}_TeamField`}
                        />
                      ),
                      contract_number: <TextField data-testid={`${testId}_ContractNumberField`} />,
                      contract_type: (
                        <SelectChipField
                          choices={contractTypeChoices}
                          data-testid={`${testId}_ContractTypeField`}
                        />
                      ),
                      status: (
                        <SelectChipField
                          choices={contractStatusChoices}
                          data-testid={`${testId}_StatusField`}
                        />
                      ),
                      effective_date: <UTCDateField data-testid={`${testId}_EffectiveDateField`} />,
                      end_date: <UTCDateField data-testid={`${testId}_EndDateField`} />,
                      is_active: <BooleanField data-testid={`${testId}_IsActiveField`} />,
                      relationship_type: (
                        <SelectChipField
                          choices={contractRelationshipTypeChoices}
                          data-testid={`${testId}_RelationshipTypeField`}
                        />
                      ),
                      paper_transaction_statement_frequency: (
                        <SelectField
                          choices={paperTransactionStatementFrequencyChoices}
                          data-testid={`${testId}_PaperTransactionStatementFrequencyField`}
                        />
                      ),
                      version: <NumberField data-testid={`${testId}_VersionField`} />,
                      is_liv: <BooleanField data-testid={`${testId}_IsLivField`} />,
                      created_at: <UTCDateField data-testid={`${testId}_CreatedAtField`} />,
                      updated_at: <UTCDateField data-testid={`${testId}_UpdatedAtField`} />,
                      filib_url: <TextField data-testid={`${testId}_FilibUrlField`} />,
                      offer_code: <TextField data-testid={`${testId}_OfferCodeField`} />,
                    }}
                  />
                </ShowSection>
                <ShowSection label="resources.bo_common.b2b.contracts.menus.matching_contributions">
                  <ShowFields<GetBoExternalContractsOneResponse>
                    fields={{
                      matching_contributions_enabled: (
                        <BooleanField data-testid={`${testId}_MatchingContributionsEnabledField`} />
                      ),
                      matching_contribution_cut_off_date: (
                        <TextField data-testid={`${testId}_MatchingContributionCutOffDateField`} />
                      ),
                      matching_contribution_payment_frequency: (
                        <SelectChipField
                          choices={b2bMatchingContributionPaymentFrequencyChoices}
                          data-testid={`${testId}_MatchingContributionPaymentFrequencyField`}
                        />
                      ),
                      total_contribution_ceiling: (
                        <CurrencyField
                          currency="EUR"
                          source=""
                          data-testid={`${testId}_TotalContributionCeilingField`}
                        />
                      ),
                      is_matching_contribution_displayed: (
                        <BooleanField
                          data-testid={`${testId}_IsMatchingContributionDisplayedField`}
                        />
                      ),
                    }}
                  />
                </ShowSection>
              </VerticalTab>
              <VerticalTab
                icon={<ContactPageIcon />}
                data-testid="BoExternalContractsShow_DistributorVerticalTab"
                label="resources.bo_common.b2b.contracts.menus.distributor.label"
                path="distributor">
                <ShowSection label="resources.bo_common.b2b.contracts.menus.distributor.headers.distributor_correspondent">
                  <ShowFields<GetBoExternalContractsOneResponse>
                    fields={{
                      dist_correspondent_lastname: (
                        <TextField data-testid={`${testId}_DistCorrespondentLastnameField`} />
                      ),
                      dist_correspondent_firstname: (
                        <TextField data-testid={`${testId}_DistCorrespondentFirstnameField`} />
                      ),
                      dist_correspondent_portfolio_code: (
                        <TextField data-testid={`${testId}_DistCorrespondentPortfolioCodeField`} />
                      ),
                      dist_correspondent_orias_code: (
                        <TextField data-testid={`${testId}_DistCorrespondentOriasCodeField`} />
                      ),
                      dist_correspondent_fia_code: (
                        <TextField data-testid={`${testId}_DistCorrespondentFiaCodeField`} />
                      ),
                      dist_correspondent_email: (
                        <Stack direction="row" spacing={2} alignItems={'center'}>
                          <TextField
                            source="dist_correspondent_email"
                            data-testid={`${testId}_DistCorrespondentEmailField`}
                          />
                          <EmailStateChip
                            source="dist_correspondent_email_state"
                            emailSource="dist_correspondent_email"
                            logResource="b2b_contract"
                          />
                        </Stack>
                      ),
                      dist_correspondent_landline: (
                        <TextField data-testid={`${testId}_DistCorrespondentLandlineField`} />
                      ),
                      dist_correspondent_phone_number: (
                        <TextField data-testid={`${testId}_DistCorrespondentPhoneNumberField`} />
                      ),
                      dist_correspondent_collective_inspector_email: (
                        <TextField
                          data-testid={`${testId}_DistCorrespondentCollectiveInspectorEmailField`}
                        />
                      ),
                    }}
                  />
                </ShowSection>
                <ShowSection label="resources.bo_common.b2b.contracts.menus.distributor.headers.address">
                  <ShowFields<GetBoExternalContractsOneResponse>
                    fields={{
                      dist_correspondent_street: (
                        <TextField data-testid={`${testId}_DistCorrespondentStreetField`} />
                      ),
                      dist_correspondent_postal_code: (
                        <TextField data-testid={`${testId}_DistCorrespondentPostalCodeField`} />
                      ),
                      dist_correspondent_city: (
                        <TextField data-testid={`${testId}_DistCorrespondentCityField`} />
                      ),
                      dist_correspondent_country: (
                        <SelectField
                          choices={countryCodeChoicesByName}
                          data-testid={`${testId}_DistCorrespondentCountryField`}
                        />
                      ),
                    }}
                  />
                </ShowSection>
              </VerticalTab>
              <VerticalTab
                icon={<AgreementsIcon />}
                data-testid="BoExternalContractsShow_AgreementsVerticalTab"
                label="resources.bo_common.b2b.contracts.menus.agreements"
                path="agreements">
                <B2bContractAgreements
                  record={record}
                  showsProcessingOptionsByCashSource={allowsProcessingOptionsByCashSource}
                />
              </VerticalTab>
              {isERContract(record.contract_type) ? null : (
                <VerticalTab
                  icon={<PlansIcon />}
                  label="resources.contracts.menus.plans"
                  path="plans">
                  <B2bPlans resource="companies/plans" />
                </VerticalTab>
              )}
              <VerticalTab
                icon={<ArticleIcon />}
                label="resources.bo_common.b2b.contracts.menus.documents"
                path="documents">
                <ContractsDocumentsList />
              </VerticalTab>
              {!isERContract(record.contract_type) && (
                <VerticalTab
                  icon={<FeesIcon />}
                  label="resources.bo_common.b2b.contracts.menus.fees"
                  data-testid="BoExternalContractsShow_FeesVerticalTab"
                  path="fees">
                  {match(record.fees?.custody.custody_config)
                    .with('standard', 'custom_all_plans', () => (
                      <ShowSection
                        label="resources.bo_common.b2b.contracts.fees.sections.custody"
                        subtitle={
                          record.fees?.custody.custody_config === 'custom_all_plans'
                            ? 'resources.bo_common.b2b.contracts.fees.subtitles.amounts_per_employees'
                            : undefined
                        }>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.employee_status',
                                )}
                              </TableCell>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.paid_by',
                                )}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.current',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_current_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByCurrentField`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.past',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_past_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByPastField`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.retired',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_retired_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByRetiredField`}
                                />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </ShowSection>
                    ))
                    .with('custom_per_plan', () => (
                      <ShowSection
                        label="resources.bo_common.b2b.contracts.fees.sections.custody"
                        subtitle="resources.bo_common.b2b.contracts.fees.subtitles.amounts_per_products">
                        <ShowSection
                          label="resources.bo_common.b2b.companies.saving_plans.types_short.PEE"
                          sx={{ margin: '10px' }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.employee_status',
                                  )}
                                </TableCell>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.paid_by',
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.current',
                                  )}
                                </TableCell>
                                <TableCell>
                                  <SelectChipField
                                    source="fees.custody.paid_by_for_current_employees_pee"
                                    choices={feePaidByChoices}
                                    data-testid={`${testId}_FeesPeePaidByCurrentField`}
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.past',
                                  )}
                                </TableCell>
                                <TableCell>
                                  <SelectChipField
                                    source="fees.custody.paid_by_for_past_employees_pee"
                                    choices={feePaidByChoices}
                                    data-testid={`${testId}_FeesPeePaidByPastField`}
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.retired',
                                  )}
                                </TableCell>
                                <TableCell>
                                  <SelectChipField
                                    source="fees.custody.paid_by_for_retired_employees_pee"
                                    choices={feePaidByChoices}
                                    data-testid={`${testId}_FeesPeePaidByRetiredField`}
                                  />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </ShowSection>
                        <ShowSection
                          label="resources.bo_common.b2b.companies.saving_plans.types_short.PERECO"
                          sx={{ margin: '10px' }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.employee_status',
                                  )}
                                </TableCell>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.paid_by',
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.current',
                                  )}
                                </TableCell>
                                <TableCell>
                                  <SelectChipField
                                    source="fees.custody.paid_by_for_current_employees_pereco"
                                    choices={feePaidByChoices}
                                    data-testid={`${testId}_FeesPerecoPaidByCurrentField`}
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.past',
                                  )}
                                </TableCell>
                                <TableCell>
                                  <SelectChipField
                                    source="fees.custody.paid_by_for_past_employees_pereco"
                                    choices={feePaidByChoices}
                                    data-testid={`${testId}_FeesPerecoPaidByPastField`}
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ height: '50px' }}>
                                <TableCell>
                                  {translate(
                                    'resources.bo_common.b2b.contracts.fees.custody.paid_by.retired',
                                  )}
                                </TableCell>
                                <TableCell>
                                  <SelectChipField
                                    source="fees.custody.paid_by_for_retired_employees_pereco"
                                    choices={feePaidByChoices}
                                    data-testid={`${testId}_FeesPerecoPaidByRetiredField`}
                                  />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </ShowSection>
                      </ShowSection>
                    ))
                    .with('custom_flat_rate', () => (
                      <ShowSection label="resources.bo_common.b2b.contracts.fees.sections.custody">
                        <Table>
                          <TableHead>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.employee_status',
                                )}
                              </TableCell>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.paid_by',
                                )}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.current',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_current_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByCurrentField`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.past',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_past_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByPastField`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.retired',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_retired_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByRetiredField`}
                                />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </ShowSection>
                    ))
                    .with('custom_old_pricing', () => (
                      <ShowSection label="resources.bo_common.b2b.contracts.fees.sections.custody">
                        <Table>
                          <TableHead>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.employee_status',
                                )}
                              </TableCell>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.paid_by',
                                )}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.current',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_current_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByCurrentField`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.past',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_past_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByPastField`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ height: '50px' }}>
                              <TableCell>
                                {translate(
                                  'resources.bo_common.b2b.contracts.fees.custody.paid_by.retired',
                                )}
                              </TableCell>
                              <TableCell>
                                <SelectChipField
                                  source="fees.custody.paid_by_for_retired_employees"
                                  choices={feePaidByChoices}
                                  data-testid={`${testId}_FeesPaidByRetiredField`}
                                />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </ShowSection>
                    ))
                    .with(undefined, () => <></>)
                    .exhaustive()}
                </VerticalTab>
              )}
              <VerticalTab
                icon={<Timeline />}
                label="resources.bo_common.b2b.investor_profile.menu.investor_profile"
                path="investor_profile">
                <B2BInvestorProfileVerticalTabContent record={record} />
              </VerticalTab>
            </VerticalTabsLayout>
          );
        }}
      />
    </ShowWithNavigation>
  );
};
